import React, { useEffect, useRef, useState } from "react";
import { ShoppingCart } from "lucide-react";
import { GoTriangleDown, GoTriangleUp } from "react-icons/go";
import { FiShoppingCart } from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Search from "./Search";
import Usermenu from "./Usermenu";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();

  const isSearchPage = location.pathname === "/search";
  const isOnLayoutPage = location.pathname.startsWith("/Layout");

  const userFromStore = useSelector((state) => state?.user?.data ?? state?.user);
  const isLoggedIn = Boolean(userFromStore && userFromStore._id);

  const [openUserMenu, setOpenUserMenu] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close user menu on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenUserMenu(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Close user menu when navigating
  useEffect(() => {
    setOpenUserMenu(false);
  }, [location.pathname]);

  const handleCartOpen = () => {
    console.log("Cart icon clicked");
    setIsMenuOpen(false);
    // hook into your CartDrawer context here if needed
  };

  return (
    <header className="bg-white shadow-md py-3 px-4 md:px-8 sticky top-0 z-50">
      <div className="mx-auto flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/assets/Images/logoimg.png"
              alt="Laminance logo"
              className="h-10 w-auto"
            />
         
          </Link>
        </div>

        {/* Search Bar (hidden on /search, hidden on mobile) */}
        {/* {!isSearchPage && (
          <div className="hidden md:block flex-1 px-6">
            <Search />
          </div>
        )} */}

        {/* Desktop Nav Links */}
        <nav className="hidden md:flex space-x-6">
          <Link to="/" className="text-gray-700 hover:text-orange-500">
            Home
          </Link>
          <Link to="/contact" className="text-gray-700 hover:text-orange-500">
            Contact
          </Link>
          <Link to="/gallery" className="text-gray-700 hover:text-orange-500">
            Gallery
          </Link>
        </nav>

        {/* Right Side */}
        <div className="flex items-center space-x-4">
          {/* Select Style / Cart (depends on Layout page) */}
          {!isOnLayoutPage ? (
            <Link
              to="/catalog"
              className="hidden md:block bg-[#30465a] text-white px-4 py-2 rounded hover:bg-[#243a4a]"
            >
              Select Your Style
            </Link>
          ) : (
            <button
              onClick={handleCartOpen}
              className="hidden md:flex items-center gap-2 border border-orange-500 text-orange-500 px-3 py-2 rounded hover:bg-orange-50"
            >
              <FiShoppingCart size={20} />
              <span className="hidden lg:inline">Cart</span>
            </button>
          )}

          {/* Cart Icon (always visible) */}
          <button className="relative md:hidden" aria-label="Cart">
            <ShoppingCart className="w-6 h-6 text-gray-600" />
            <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full px-1">
              3
            </span>
          </button>

          {/* Account/Login */}
          {isLoggedIn ? (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setOpenUserMenu((s) => !s)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition cursor-pointer"
                aria-haspopup="menu"
                aria-expanded={openUserMenu}
              >
                <span>Account</span>
                {openUserMenu ? <GoTriangleUp /> : <GoTriangleDown />}
              </button>

              {openUserMenu && (
                <div className="absolute right-0 mt-2 bg-white rounded-xl shadow-lg p-4 min-w-52">
                  <Usermenu />
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="bg-[#284563] hover:bg-[#1e2f3b] text-white px-4 py-2 rounded transition"

            >
              Login
            </button>
          )}

          {/* Mobile Hamburger */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-orange-500"
              aria-label="Toggle Navigation Menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 5.25h16.5m-16.5 6h16.5m-16.5 6h16.5"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <nav className="md:hidden bg-white shadow-md mt-4 px-4 py-3 space-y-4 rounded-md">
          <Link
            to="/"
            className="block text-gray-700 hover:text-orange-500"
            onClick={() => setIsMenuOpen(false)}
          >
            Home
          </Link>
          <Link
            to="/contact"
            className="block text-gray-700 hover:text-orange-500"
            onClick={() => setIsMenuOpen(false)}
          >
            Contact
          </Link>
          <Link
            to="/gallery"
            className="block text-gray-700 hover:text-orange-500"
            onClick={() => setIsMenuOpen(false)}
          >
            Gallery
          </Link>

          {!isOnLayoutPage ? (
            <Link
              to="/catalog"
              className="block bg-[#30465a] text-white px-4 py-2 rounded hover:bg-[#243a4a] text-center"
              onClick={() => setIsMenuOpen(false)}
            >
              Select Your Style
            </Link>
          ) : (
            <button
              onClick={handleCartOpen}
              className="w-full flex justify-center items-center gap-2 bg-[#30465a] text-white px-4 py-2 rounded hover:bg-[#243a4a] text-center"
            >
              <FiShoppingCart size={20} />
              <span>Cart</span>
            </button>
          )}
        </nav>
      )}
    </header>
  );
}
